import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage } from '../../../util/reactIntl';
import { generateLinkProps } from '../../../util/routes';
import { getListingRejection } from '../../../util/listingReview';
import { isDeletedListing } from '../../../util/listingDeletion';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  propTypes,
} from '../../../util/types';

import { NamedLink, ExternalLink, InlineTextButton } from '../../../components';
import EditIcon from '../EditIcon';

import css from '../ListingPage.module.css';

/**
 * CTAButtonMaybe component renders a call-to-action (CTA) button if it is enabled.
 * If the link is internal, a `NamedLink` is rendered, otherwise an `ExternalLink`
 * is rendered. Uses userData to inject user data into the URL.
 */
const CTAButtonMaybe = props => {
  const { data, routeConfiguration, userId, userEmail, listingId, isPendingApproval } = props;

  // If the call to action button is not enabled, return null and don't render anything
  const hasValidType = data?.type && data.type !== 'none';
  const isCTAEnabled = hasValidType && isPendingApproval;

  if (!isCTAEnabled) {
    return null;
  }

  const { type, text, href } = data;

  // Construct a the props for the NamedLink and ExternalLink components dynamically using the CTA data and user info
  const ctaLink = generateLinkProps(type, href, routeConfiguration, userId, userEmail, listingId);

  const isInternalLink = type === 'internal' && ctaLink.route;

  return isInternalLink ? (
    <NamedLink
      name={ctaLink.route.name}
      to={ctaLink.route.to}
      params={ctaLink.route.params}
      className={css.actionBarCTA}
    >
      {text}
    </NamedLink>
  ) : (
    <ExternalLink href={ctaLink.link} className={css.actionBarCTA}>
      {text}
    </ExternalLink>
  );
};

/**
 * The ActionBar component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} props.isOwnListing - Whether the listing is own
 * @param {propTypes.listing | propTypes.ownListing} props.listing - The listing
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {Object} props.editParams - The path params for the named route to edit the listing
 * @param {string} props.editParams.id - The id
 * @param {string} props.editParams.slug - The slug
 * @param {'edit' | 'draft'} props.editParams.type - The type
 * @param {string} props.editParams.tab - The tab (e.g. 'details' or 'pricing')
 * @param {boolean} props.showNoPayoutDetailsSet - Show info about missing payout details
 * @param {boolean} [props.isJustSubmitted] - The seller arrived straight from publishing it
 * @param {function} [props.onDeleteListing] - Opens "Slett annonse" for the seller's own listing
 * @returns {JSX.Element} action bar component
 */
export const ActionBar = props => {
  const {
    rootClassName,
    className,
    isOwnListing,
    listing,
    currentUser,
    editParams,
    showNoPayoutDetailsSet,
    isJustSubmitted = false,
    onDeleteListing,
  } = props;
  const classes = classNames(rootClassName || css.actionBar, className);

  const state = listing.attributes.state;
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isDraft = state === LISTING_STATE_DRAFT;
  // An admin sent it back: pending all the same, but waiting on the seller.
  const rejection = getListingRejection(listing);
  const needsChanges = isPendingApproval && !!rejection;

  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const approvalToPublishOptions =
    config?.accessControl?.listings?.requireApprovalToPublishOptions?.callToAction || {};

  if (isOwnListing && showNoPayoutDetailsSet) {
    return (
      <div className={classNames(classes, css.actionBarAttention)}>
        <p className={classNames(css.ownListingText, css.missingPayoutDetailsText)}>
          <FormattedMessage id="ListingPage.addPayoutDetailsMessage" />
        </p>
        <NamedLink className={css.addPayoutDetails} name="StripePayoutPage">
          <EditIcon className={css.editIcon} />
          <FormattedMessage id="ListingPage.addPayoutDetails" />
        </NamedLink>
      </div>
    );
  } else if (isOwnListing && isPendingApproval && isJustSubmitted && !needsChanges) {
    // The receipt right after publishing. Just the good news: no share link
    // (a pending listing's page is visible to its seller only) and no edit
    // link, which returns with the ordinary pending bar on the next visit.
    return (
      <div className={classNames(classes, css.actionBarSubmitted)} role="status">
        <div className={css.submittedMain}>
          <span className={css.submittedIcon} aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5.5 12.5 10 17l8.5-9.5" />
            </svg>
          </span>
          <div className={css.submittedText}>
            <p className={css.submittedHeading}>
              <span className={css.desktopOnly}>
                <FormattedMessage id="ListingPage.submittedHeading" />
              </span>
              <span className={css.mobileOnly}>
                <FormattedMessage id="ListingPage.submittedHeadingMobile" />
              </span>
            </p>
            <p className={css.submittedBody}>
              <span className={css.mobileOnly}>
                <FormattedMessage id="ListingPage.submittedPendingMobile" />
              </span>
              <FormattedMessage id="ListingPage.submittedEmail" />
            </p>
          </div>
        </div>
      </div>
    );
  } else if (isOwnListing && isDeletedListing(listing)) {
    // Deleted by the seller: nothing left to edit or do here.
    return (
      <div className={classes}>
        <p className={css.ownListingText}>
          <FormattedMessage id="ListingPage.ownDeletedListing" />
        </p>
      </div>
    );
  } else if (isOwnListing) {
    // A published listing gets no text, just the links: the buy button below
    // already says "Dette er din annonse".
    let ownListingTextTranslationId = null;

    if (needsChanges) {
      ownListingTextTranslationId = 'ListingPage.ownListingNeedsChanges';
    } else if (isPendingApproval) {
      ownListingTextTranslationId = 'ListingPage.ownListingPendingApproval';
    } else if (isClosed) {
      ownListingTextTranslationId = 'ListingPage.ownClosedListing';
    } else if (isDraft) {
      ownListingTextTranslationId = 'ListingPage.ownListingDraft';
    }

    const message = isDraft ? 'ListingPage.finishListing' : 'ListingPage.editListing';

    const hasValidType = approvalToPublishOptions?.type && approvalToPublishOptions.type !== 'none';
    const isCTAEnabled = hasValidType && isPendingApproval;

    return (
      <div
        className={classNames(classes, {
          // Pending-approval gets the same attention treatment (bg + border)
          // as the missing-payout bar, rather than recolouring the text.
          [css.actionBarAttention]: isPendingApproval && !needsChanges,
          // Sent back by an admin: the same red as the reason box on the
          // admin's "Sendt tilbake" tab.
          [css.actionBarFail]: needsChanges,
          [css.actionBarWithCTAEnabled]: isCTAEnabled,
          [css.actionBarLinksOnly]: !ownListingTextTranslationId,
        })}
      >
        {ownListingTextTranslationId ? (
          <p className={classNames(css.ownListingText, { [css.CTAEnabled]: isCTAEnabled })}>
            <FormattedMessage
              id={ownListingTextTranslationId}
              values={{ reason: needsChanges ? rejection.reason : null }}
            />
            {needsChanges && rejection.personalMessage ? (
              <>
                {' '}
                <FormattedMessage
                  id="ListingPage.ownListingRejectionMessage"
                  values={{ message: rejection.personalMessage }}
                />
              </>
            ) : null}
          </p>
        ) : null}
        <span className={isCTAEnabled ? css.linkContainer : css.noShrink}>
          <NamedLink
            className={classNames(css.editListingLink, { [css.CTAEnabled]: isCTAEnabled })}
            name="EditListingPage"
            params={editParams}
          >
            <EditIcon className={css.editIcon} />
            <FormattedMessage id={message} />
          </NamedLink>
          <CTAButtonMaybe
            data={approvalToPublishOptions}
            routeConfiguration={routeConfiguration}
            listingId={listing?.id?.uuid}
            userId={currentUser?.id?.uuid}
            userEmail={currentUser?.attributes?.email}
            isPendingApproval={isPendingApproval}
          />
          {onDeleteListing && !isDraft ? (
            <InlineTextButton
              type="button"
              id="ListingPage.deleteListingButton"
              rootClassName={css.deleteListingButton}
              onClick={onDeleteListing}
            >
              <FormattedMessage id="ListingPage.deleteListing" />
            </InlineTextButton>
          ) : null}
        </span>
      </div>
    );
  } else if (isClosed) {
    return (
      <div className={classes}>
        <p className={css.closedListingText}>
          <FormattedMessage id="ListingPage.closedListing" />
        </p>
      </div>
    );
  }
  return null;
};

export default ActionBar;
